import React from 'react';
import apiClient, { getApiUrl } from '../../utils/apiClient';
import QRCode from 'qrcode';



interface ReceiptPrintProps {
  transaction: any;
  onClose: () => void;
}

// Electron環境チェック
declare global {
  interface Window {
    electronAPI?: {
      printReceipt: (html: string) => Promise<any>;
      isElectron: boolean;
    };
  }
}

// ポイント設定を取得（キャッシュ付き）
let cachedPointsPerYen: number | null = null;
let cacheExpiry: number = 0;

const getPointsPerYen = async (): Promise<number> => {
  // キャッシュが有効な場合は使用
  if (cachedPointsPerYen !== null && Date.now() < cacheExpiry) {
    return cachedPointsPerYen;
  }

  try {
    const token = localStorage.getItem('waton_token');
    if (token) {
      const response = await fetch(`${getApiUrl()}/point-settings/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        cachedPointsPerYen = data.points_per_yen || 1000;
        cacheExpiry = Date.now() + 5 * 60 * 1000; // 5分間キャッシュ
        return cachedPointsPerYen;
      }
    }
  } catch (error) {
    // console.error('ポイント設定取得エラー:', error);
  }
  
  // エラー時はデフォルト値
  return 1000;
};

export const printReceiptHTML = async (transaction: any) => {
  // ポップアップブロック回避：awaitより前にウィンドウを確保
  let printWindow: Window | null = null;
  if (!window.electronAPI?.isElectron) {
    printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('ポップアップがブロックされています。ブラウザの設定を確認してください。');
      return;
    }
    // 空白ウィンドウによる参照切れ防止：即座にプレースホルダを書き込む
    printWindow.document.write('<!doctype html><html><head><meta charset="utf-8"><title>印刷準備中</title></head><body>読み込み中…</body></html>');
    printWindow.document.close();
  }

  // ポイント計算（transaction.pointsがない場合のフォールバック）
  let displayPoints = transaction.points;
  if (displayPoints === undefined || displayPoints === null) {
    try {
      // 動的にポイント設定を取得（2025-11-16修正）
      const pointsPerYen = await getPointsPerYen();
      // 値引き後の金額（final_total）でポイント計算（2025-11-15修正）
      displayPoints = Math.floor((transaction.final_total || transaction.total) / pointsPerYen);
      transaction.points = displayPoints;
    } catch {
      displayPoints = Math.floor((transaction.final_total || transaction.total) / 1000);
      transaction.points = displayPoints;
    }
  }

  // QRコード生成（claim_idがあればセキュア版、なければtransactionId版）
  const liffId = '2008033618-mddZrLzG';
  const version = '2.1';
  
  // すでにclaim_idがある場合（事前生成済み）はそれを使用
  const linePointsUrl = transaction.claimId
    ? `https://liff.line.me/${liffId}?claimId=${transaction.claimId}&v=${version}`
    : `https://liff.line.me/${liffId}?transactionId=${transaction.id}&v=${version}`;

  let qrCodeDataUrl = '';
  try {
    qrCodeDataUrl = await QRCode.toDataURL(linePointsUrl, {
      width: 100,
      margin: 1,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      }
    });
  } catch (error) {
    console.error('QRコード生成エラー:', error);
  }

  const subtotalNum = Number(transaction.subtotal || 0);
  const taxNum = Number(transaction.tax || 0);
  let displayTaxRate = Number(transaction.tax_rate);
  if (!(Number.isFinite(displayTaxRate) && displayTaxRate > 0)) {
    if (subtotalNum > 0 && Number.isFinite(subtotalNum) && Number.isFinite(taxNum)) {
      const calculatedRate = Math.round((taxNum / subtotalNum) * 100);
      if (calculatedRate > 0 && calculatedRate <= 100) {
        displayTaxRate = calculatedRate;
      }
    }
  }
  if (!(Number.isFinite(displayTaxRate) && displayTaxRate > 0)) {
    displayTaxRate = transaction.order_type === 'takeout' ? 8 : 10;
  }
  const displayTaxRateLabel = Math.round(displayTaxRate);

  const receiptHTML = `
<!DOCTYPE html>
<html lang="ja">
<head>
    <meta charset="UTF-8">
    <title>レシート - とんかつ和とん</title>
    <style>
        @page {
            margin: 0;
        }
        
        @media print {
            body {
                margin: 0;
                padding: 0;
            }
            .no-print {
                display: none !important;
            }
        }
        
        body {
            font-family: 'MS Gothic', 'ヒラギノ角ゴ Pro', monospace;
            margin: 0;
            padding: 0;
            width: 80mm;
            font-size: 14px;  /* 12pxから14pxに拡大 */
            line-height: 1.5;  /* 1.4から1.5に調整 */
        }
        
        .receipt {
            padding: 5mm;
            width: 70mm;
        }
        
        .header {
            text-align: center;
            margin-bottom: 3mm;
        }
        
        .store-name {
            font-size: 24px;  /* 18pxから24pxに拡大 */
            font-weight: bold;
            margin: 2mm 0;
        }
        
        .store-info {
            font-size: 14px;  /* 12pxから14pxに拡大 */
            color: #000;  /* 黒色に変更 */
            font-weight: bold;
        }
        
        .divider {
            border-top: 1px dashed #000;
            margin: 3mm 0;
        }
        
        .thick-divider {
            border-top: 2px solid #000;
            margin: 3mm 0;
        }
        
        .info-row {
            display: flex;
            justify-content: space-between;
            margin: 1mm 0;
            font-size: 13px;  /* 11pxから13pxに拡大 */
        }
        
        .items {
            margin: 3mm 0;
        }
        
        .item {
            display: flex;
            justify-content: space-between;
            margin: 2mm 0;  /* 余白を少し増やす */
            font-size: 14px;  /* 11pxから14pxに拡大 */
        }
        
        .item-name {
            flex: 1;
            font-weight: bold;  /* 商品名を太字に */
        }
        
        .item-details {
            font-size: 12px;  /* 10pxから12pxに拡大 */
            color: #000;  /* 薄い#666から黒に変更 */
            margin-left: 5mm;
            font-weight: bold;  /* 計算式を太字に */
        }
        
        .totals {
            margin: 3mm 0;
        }
        
        .total-row {
            display: flex;
            justify-content: space-between;
            margin: 1mm 0;
            font-size: 14px;  /* 11pxから14pxに拡大 */
            font-weight: bold;
        }
        
        .grand-total {
            font-size: 20px;  /* 14pxから20pxに拡大 */
            font-weight: bold;
            margin: 3mm 0;
        }
        
        .payment {
            margin: 3mm 0;
            font-size: 14px;  /* 11pxから14pxに拡大 */
        }
        
        .payment .change-row {
            font-size: 18px;  /* お釣りは特に大きく */
            font-weight: bold;
        }
        
        .footer {
            text-align: center;
            margin-top: 5mm;
            font-size: 14px;  /* 10pxから14pxに拡大 */
            font-weight: bold;
        }
        
        .qr-code {
            text-align: center;
            margin: 5mm 0;
        }
        
        .qr-code img {
            width: 25mm;  /* 40mmから25mmに縮小 */
            height: 25mm;  /* 40mmから25mmに縮小 */
        }
        
        .qr-label {
            font-size: 11px;  /* 9pxから11pxに拡大 */
            margin-top: 2mm;
        }
    </style>
</head>
<body>
    <div class="receipt">
        <div class="header">
            <div class="store-name">とんかつ和とん</div>
            <div class="store-info">
                <div>三重県鈴鹿市中江島町19-38</div>
                <div>TEL: 059-389-7787</div>
            </div>
        </div>
        
        <div class="thick-divider"></div>
        
        <div class="info-row">
            <span>${new Date(transaction.timestamp || new Date()).toLocaleString('ja-JP')}</span>
        </div>
        <div class="info-row">
            <span>レシート番号: ${String(transaction.id).padStart(8, '0')}</span>
            <span>${transaction.order_type === 'takeout' ? 'テイクアウト' : '店内飲食'}</span>
        </div>
        
        <div class="divider"></div>
        
        <div class="items">
            ${(transaction.items || []).map(item => `
                <div class="item">
                    <div class="item-name">
                        ${item.name}
                        <div class="item-details">
                            @${(item.price || 0).toLocaleString()} × ${item.quantity || 0}
                        </div>
                    </div>
                    <div>¥${((item.price || 0) * (item.quantity || 0)).toLocaleString()}</div>
                </div>
            `).join('')}
        </div>
        
        <div class="divider"></div>
        
        <div class="totals">
            <div class="total-row">
                <span>小計</span>
                <span>¥${(transaction.subtotal || 0).toLocaleString()}</span>
            </div>
            ${transaction.tax_mode !== 'exempt' && !transaction.is_tax_exempt ? `
            <div class="total-row">
                <span>${(transaction.tax_mode === 'external') ? `消費税（${displayTaxRateLabel}%）` : `(内消費税 ${displayTaxRateLabel}%)`}</span>
                <span>¥${(transaction.tax || 0).toLocaleString()}</span>
            </div>` : ''}
            ${transaction.discount_amount && transaction.discount_amount > 0 ? `
            <div class="total-row" style="margin-top: 8px;">
                <span>値引き${transaction.coupon_discount > 0 ? '（クーポン）' : transaction.manual_discount > 0 ? '（店舗）' : ''}</span>
                <span>-¥${transaction.discount_amount.toLocaleString()}</span>
            </div>` : ''}
        </div>

        <div class="thick-divider"></div>

        <div class="total-row grand-total" style="font-size: 22px;">
            <span>お支払い額</span>
            <span>¥${((transaction.final_total || transaction.total) || 0).toLocaleString()}</span>
        </div>

        <div class="thick-divider"></div>

        <div class="payment">
            <div class="info-row">
                <span>お支払い方法</span>
                <span>${transaction.payment_type ? (transaction.payment_type === 'cash' ? '現金' : (transaction.payment_type === 'paypay' || transaction.payment_type === 'PayPay' ? 'PayPay' : transaction.payment_type)) : '不明'}</span>
            </div>
            ${transaction.payment_type === 'cash' && transaction.received ? `
                <div class="info-row">
                    <span>お預かり</span>
                    <span>¥${(transaction.received || 0).toLocaleString()}</span>
                </div>
                <div class="info-row change-row">
                    <span>お釣り</span>
                    <span>¥${(transaction.change || 0).toLocaleString()}</span>
                </div>
            ` : ''}
        </div>
        
        ${qrCodeDataUrl ? `
            <div class="thick-divider"></div>
            
            <div class="qr-code">
                <div style="font-size: 14px; font-weight: bold; margin-bottom: 3mm;">
                    LINEポイントカード
                </div>
                <div style="font-size: 16px; font-weight: bold; color: #000; margin-bottom: 3mm;">
                    スキャンでポイント獲得！
                </div>
                <img id="qrCodeImage" src="${qrCodeDataUrl}" alt="QRコード" />
                <div class="qr-label" style="margin-top: 3mm;">
                    <div style="font-size: 18px; font-weight: bold;">
                        ${displayPoints}ポイント獲得できます
                    </div>
                    <div style="font-size: 12px; color: #000; font-weight: bold; margin-top: 2mm;">
                        ポイント獲得期限: ${new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString('ja-JP')}まで
                    </div>
                    <div style="font-size: 12px; color: #000; margin-top: 1mm;">
                        ※このQRコードは1回のみ使用可能です
                    </div>
                </div>
            </div>
        ` : ''}

        <div class="thick-divider"></div>

        <div class="footer">
            <div>ありがとうございました</div>
            <div>またのご来店をお待ちしております</div>
        </div>
    </div>
    
    <script>
        // 自動印刷（改良版）
        window.onload = function() {
            // 印刷設定
            if (window.chrome) {
                // Chromeの場合、自動印刷を試みる
                setTimeout(function() {
                    window.print();
                    // 印刷後、1秒待って自動で閉じる
                    setTimeout(function() {
                        window.close();
                    }, 1000);
                }, 100);
            } else {
                // その他のブラウザ
                window.print();
                window.onafterprint = function() {
                    window.close();
                };
            }
        };
        
        // ESCキーで閉じる
        document.addEventListener('keydown', function(e) {
            if (e.key === 'Escape') {
                window.close();
            }
        });
    </script>
</body>
</html>
`;

  // Electron環境の場合はサイレント印刷
  if (window.electronAPI?.isElectron) {
    console.log('Electron環境検出：サイレント印刷を実行');
    try {
      const result = await window.electronAPI.printReceipt(receiptHTML);
      if (result.success) {
        console.log('サイレント印刷成功');
      } else {
        console.error('サイレント印刷失敗:', result.error);
        alert('印刷に失敗しました');
      }
    } catch (error) {
      console.error('印刷エラー:', error);
      alert('印刷エラーが発生しました');
    }
  } else {
    // 通常のブラウザ環境（printWindowはawait前に確保済み）
    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(receiptHTML);
      printWindow.document.close();
    }
  }
};

const ReceiptPrint: React.FC<ReceiptPrintProps> = ({ transaction, onClose }) => {
  const handlePrint = async () => {
    await printReceiptHTML(transaction);
    onClose();
  };

  return (
    <button 
      onClick={handlePrint}
      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
    >
      レシート印刷
    </button>
  );
};

export default ReceiptPrint;
